import React, { useEffect, useState } from "react";
import SettingsFields from "./SettingsFields";
import {
  Box,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  useTheme,
  Typography,
  Alert,
} from "@mui/material";
import { createYupSchema } from "../../utilities/yupSchemaCreator";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { useSite } from "./Provider";
import ErrorsList from "../../ui/ErrorsList";
import { LoadingSpinner } from "../../ui/LoadingSpinner"; // <-- import your spinner

const yupSchema = SettingsFields.reduce(createYupSchema, {});
const formSchema = Yup.object().shape(yupSchema);

const SettingsForm = ({ handleClose, siteInfo }) => {
  const theme = useTheme();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // <-- loading state

  const {
    state: { settingsInfo },
    fetchSettingsInfo,
    updateSettings,
  } = useSite();

  // ===== REACT HOOK FORM =====
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {}, // empty initially, will be reset later
    mode: "onChange",
  });

  // ===== FETCH SETTINGS WHEN SITE CHANGES =====
  useEffect(() => {
    if (!siteInfo) return;

    const loadSettings = async () => {
      setLoading(true);
      try {
        await fetchSettingsInfo(siteInfo.id);
      } catch (err) {
        setError({ title: "Failed to load settings" });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [siteInfo, fetchSettingsInfo]);

  // ===== UPDATE FORM WHEN SETTINGS INFO LOADS =====
  useEffect(() => {
    if (settingsInfo && Object.keys(settingsInfo).length > 0) {
      // Build default values from SettingsFields
      const defaultValues = SettingsFields.reduce((acc, field) => {
        acc[field.name] =
          settingsInfo[field.name] ?? (field.switch ? false : 0);
        return acc;
      }, {});
      reset(defaultValues);
    }
  }, [settingsInfo, reset]);

  // ===== SUBMIT =====
  const onSubmit = async (values) => {
    try {
      await updateSettings(siteInfo?.id, values);
      handleClose();
    } catch (error) {
      setError({
        title: error.errors?.message || error.title || "Something went wrong",
      });
    }
  };

  // ===== SHOW LOADING SPINNER =====
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  // ===== RENDER FORM =====
  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography
        id="modal-modal-description"
        sx={{ textAlign: "center", fontWeight: 600, mb: 2 }}
      >
        {siteInfo?.siteName} Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ m: 3 }}>
          <ErrorsList error={error} />
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {SettingsFields.map((field) => (
          <Box key={field.name}>
            {field.switch ? (
              <Controller
                name={field.name}
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        onBlur={onBlur}
                        inputRef={ref}
                        disabled={siteInfo?.country !== "Lebanon"}
                        color="success"
                      />
                    }
                    label={field.label}
                  />
                )}
              />
            ) : (
              <Controller
                name={field.name}
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <TextField
                    label={field.label}
                    type="number"
                    size="small"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    error={!!errors[field.name]}
                    helperText={errors[field.name]?.message}
                    sx={{
                      width: theme.dimensions?.width || "100%",
                      mt: theme.dimensions?.mt || 1,
                      mb: theme.dimensions?.mb || 1,
                    }}
                    InputProps={{
                      inputProps: {
                        min: field.min,
                        max: field.max,
                        step: field.step || 1,
                      },
                    }}
                  />
                )}
              />
            )}
          </Box>
        ))}

        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            disabled={isSubmitting}
            sx={{
              backgroundColor: theme.color.blackBackground,
              color: "#FFFF",
              width: "100%",
              mb: 1,
              "&:hover": { background: theme.color.black },
            }}
          >
            {isSubmitting ? "Submitting..." : "Save"}
          </Button>
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              background: theme.color.gray200,
              color: "black",
              width: "100%",
              "&:hover": { background: theme.color.gray300 },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default SettingsForm;
